import { useState, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShoppingCart, Bank, UploadSimple, Check, CreditCard } from "@phosphor-icons/react";

export default function BuyProductDialog({ product, clubs = [], open, onOpenChange, onSuccess }) {
  const { user } = useAuth();
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [step, setStep] = useState("quantity"); // quantity -> payment
  const [orderId, setOrderId] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const reset = () => {
    setQty(1); setNotes(""); setFile(null); setStep("quantity"); setOrderId(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const total = (product?.price || 0) * qty;
  const firstClabe = clubs.find((c) => c.clabe_bbva)?.clabe_bbva || "";
  const firstWhatsapp = clubs.find((c) => c.whatsapp)?.whatsapp || "";

  const createOrder = async () => {
    if (qty < 1 || qty > (product.stock || 0)) {
      toast.error(`Cantidad invalida (stock: ${product.stock})`);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/shop/orders", {
        items: [{ product_id: product.id, quantity: qty }],
        notes,
      });
      setOrderId(data.id);
      setStep("payment");
      toast.success("Pedido creado. Ahora sube el comprobante de pago.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear pedido");
    } finally {
      setLoading(false);
    }
  };

  const uploadProof = async () => {
    if (!file) { toast.error("Selecciona un archivo"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    setLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/shop/orders/${orderId}/upload-proof`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Comprobante enviado. En revision.");
      onOpenChange(false);
      reset();
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir");
    } finally {
      setLoading(false);
    }
  };

  const payWithStripe = async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/shop/orders/${orderId}/stripe-checkout`, { origin_url: window.location.origin });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("No se pudo iniciar el pago");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error iniciando pago");
      setLoading(false);
    }
  };

  if (!product) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md" data-testid="buy-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase flex items-center gap-2">
            <ShoppingCart size={20} /> Comprar: {product.name}
          </DialogTitle>
        </DialogHeader>

        {step === "quantity" ? (
          <div className="space-y-4">
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Cantidad</Label>
              <Input
                type="number" min="1" max={product.stock} value={qty}
                onChange={(e) => setQty(parseInt(e.target.value) || 1)}
                className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]"
                data-testid="buy-qty"
              />
              <p className="text-[10px] text-gray-500 mt-1 font-['JetBrains_Mono']">Stock disponible: {product.stock}</p>
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Notas (opcional)</Label>
              <Textarea
                value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} rows={2}
                placeholder="Talla, color, instrucciones de entrega..."
                className="bg-[#0A0A0A] border-white/20 text-white focus:border-[#CCFF00] resize-none"
                data-testid="buy-notes"
              />
            </div>
            <div className="bg-[#0A0A0A] border border-white/10 p-3 flex justify-between items-center">
              <span className="text-sm text-gray-400">Total:</span>
              <span className="text-[#CCFF00] font-['JetBrains_Mono'] font-bold text-2xl" data-testid="buy-total">${total.toFixed(2)} MXN</span>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-[#0A0A0A] border border-[#CCFF00]/40 p-4 space-y-3">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] flex items-center gap-2">
                <CreditCard size={14} weight="fill" /> Pago rapido con tarjeta
              </p>
              <p className="text-xs text-gray-400">Paga con tarjeta de credito/debito en checkout seguro de Stripe. Tu pedido se confirma al instante.</p>
              <Button
                type="button"
                onClick={payWithStripe}
                disabled={loading}
                className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-xs h-11"
                data-testid="buy-stripe-btn"
              >
                <CreditCard size={16} className="mr-2" />
                {loading ? "Redirigiendo..." : `Pagar $${total.toFixed(2)} MXN con tarjeta`}
              </Button>
            </div>

            <div className="relative flex items-center py-1">
              <div className="flex-1 border-t border-white/10"></div>
              <span className="mx-3 text-[10px] uppercase tracking-widest text-gray-500">o transferencia bancaria</span>
              <div className="flex-1 border-t border-white/10"></div>
            </div>

            <div className="bg-[#0A0A0A] border border-white/10 p-4 space-y-2">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-gray-300">Transferencia CLABE</p>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Monto a transferir:</span>
                <span className="text-white font-['JetBrains_Mono'] font-bold">${total.toFixed(2)} MXN</span>
              </div>
              {firstClabe ? (
                <div className="flex items-start gap-2 text-sm pt-2 border-t border-white/10">
                  <Bank size={16} className="text-blue-400 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-[10px] uppercase tracking-wider text-gray-500">CLABE BBVA</p>
                    <p className="text-white font-['JetBrains_Mono'] text-sm break-all">{firstClabe}</p>
                    <button
                      type="button"
                      onClick={() => { navigator.clipboard.writeText(firstClabe); toast.success("CLABE copiada"); }}
                      className="text-[10px] text-[#CCFF00] hover:underline uppercase tracking-wider"
                    >
                      Copiar
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-orange-400">El club no tiene CLABE registrada. Contacta al administrador.</p>
              )}
              {firstWhatsapp && (
                <p className="text-[11px] text-gray-500 pt-1">Dudas: <a href={`https://wa.me/52${firstWhatsapp}`} className="text-green-400 hover:underline" target="_blank" rel="noopener noreferrer">WhatsApp {firstWhatsapp}</a></p>
              )}
            </div>

            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Comprobante de transferencia (si pagaste por CLABE)</Label>
              <input
                ref={fileRef}
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="text-sm text-gray-300 file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-white/10 file:text-white file:font-bold file:uppercase file:text-xs hover:file:bg-white/20 cursor-pointer"
                data-testid="buy-proof-input"
              />
              {file && <p className="text-[10px] text-gray-500 mt-1 font-['JetBrains_Mono']">{file.name} ({(file.size / 1024).toFixed(0)} KB)</p>}
              <p className="text-[10px] text-gray-600 mt-1">Foto del recibo o PDF (max 10MB)</p>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === "quantity" ? (
            <>
              <Button variant="ghost" onClick={() => handleClose(false)} disabled={loading} className="text-gray-400 text-xs uppercase font-bold">Cancelar</Button>
              <Button onClick={createOrder} disabled={loading || product.stock < 1} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-xs" data-testid="buy-create-order">
                {loading ? "Creando..." : "Crear Pedido"}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setStep("quantity")} disabled={loading} className="text-gray-400 text-xs uppercase font-bold">Atras</Button>
              <Button onClick={uploadProof} disabled={loading || !file} variant="outline" className="border-white/10 text-gray-300 hover:bg-white/5 text-xs uppercase font-bold" data-testid="buy-upload-proof">
                {loading ? "Subiendo..." : (<><UploadSimple size={14} className="mr-1" /> Enviar comprobante</>)}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
